import React from 'react';
import { v4 as uuid } from 'uuid';
import { Office } from '../../../models';
import { Button } from '../../commons';
import { ClearIcon } from '../../icons';
import { Input } from '../../inputs';

interface ClubFormProps {
  clubs: Office[];
  setClubs: React.Dispatch<React.SetStateAction<Office[]>>;
  isReadOnly?: boolean;
}

export default function ClubForm({
  clubs,
  setClubs,
  isReadOnly = false,
}: ClubFormProps) {
  const [name, setName] = React.useState('');
  const [year, setYear] = React.useState('');
  const add = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      setClubs((_clubs) => [
        ...(_clubs ?? []),
        {
          id: uuid(),
          name,
          year,
        },
      ]);
      setName('');
      setYear('');
    },
    [name, setClubs, year]
  );

  const onDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    if (
      typeof window !== 'undefined' &&
      window.confirm('Are you sure you want to delete this qualification?')
    ) {
      const { id } = e.currentTarget.dataset;
      setClubs((club) => club.filter((q) => q.id !== id));
    }
  };

  return (
    <>
      <p className="text-lg text-neutral-600 mt-8">
        Kindly list any clubs or societies you've been a member of during your
        educational or professional career
      </p>
      <div className="mt-4">
        {isReadOnly ? null : (
          <div className="grid grid-cols-1 lg:grid-cols-[3fr_1fr_0.5fr] gap-4">
            <Input
              type="text"
              label="Club / Society"
              name="club"
              placeholder="Club / Society"
              value={name}
              onChange={(e) => {
                e.preventDefault();
                setName(e.target.value);
              }}
            />
            <Input
              type="number"
              label="Year"
              name="year"
              min={1900}
              max={2050}
              placeholder="Year"
              value={year}
              onChange={(e) => {
                e.preventDefault();
                setYear(e.target.value);
              }}
            />
            <Button
              type="button"
              color="magenta"
              className="mb-4 mt-auto"
              onClick={add}
            >
              Add
            </Button>
          </div>
        )}
        {clubs?.length > 0 ? (
          <div className="block w-full overflow-x-auto">
            <table className="items-center w-full bg-transparent border-collapse">
              <thead>
                <tr className="">
                  <th className="px-2 align-middle border border-solid py-2 text-[12px] uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left text-neutral-600">
                    Name
                  </th>
                  <th className="px-2 align-middle border border-solid py-2 text-[12px] uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left text-neutral-600">
                    Year
                  </th>
                </tr>
              </thead>
              <tbody>
                {clubs?.map((club) => (
                  <tr key={club.id}>
                    <td className="border-t-0 px-2 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap py-1 inline-flex">
                      {isReadOnly ? null : (
                        <button
                          type="button"
                          className="w-4 h-4 mr-1 my-auto text-red-600"
                          data-id={club.id}
                          onClick={onDelete}
                        >
                          <ClearIcon className="w-4 h-4" />
                        </button>
                      )}
                      <span className="my-auto">{club.name}</span>
                    </td>
                    <td className="border-t-0 px-2 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap py-1">
                      {club.year}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </>
  );
}
